import { z } from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { normalizeOptionalPhone } from '../../utils/phone';

// Base email validation
export const emailSchema = z.string()
  .min(1, 'Email is required')
  .email('Invalid email format');

// Base phone validation — country-code-only values like +234 are not complete numbers
export const phoneSchema = z.string()
  .min(1, 'Phone number is required')
  .refine((value) => isValidPhoneNumber(value), 'Enter a complete phone number');

// Base names validation
export const firstNameSchema = z.string()
  .min(1, 'First name is required')
  .min(2, 'First name must be at least 2 characters');

export const lastNameSchema = z.string()
  .min(1, 'Last name is required')
  .min(2, 'Last name must be at least 2 characters');

export const otherNamesSchema = z.string().transform(v => v.trim() === '' ? undefined : v).optional().or(z.literal('').transform(() => undefined));

// Optional name — blank allowed; if provided, must be at least 2 characters
export const optionalNameSchema = z.string()
  .transform(v => v.trim())
  .refine(v => v === '' || v.length >= 2, 'Name must be at least 2 characters if provided')
  .optional()
  .or(z.literal('').transform(() => ''));

// Gender validation
export const genderSchema = z.string()
  .refine(
    (val) => val === '' || ['Male', 'Female', 'Non-binary', 'Non-specify'].includes(val),
    'Gender must be Male, Female, Non-binary, or Non-specify'
  );

// Date of birth validation
export const dobSchema = z.string().transform(v => v.trim() === '' ? undefined : v).optional().or(z.literal('').transform(() => undefined));

// Optional email (for students — email not required)
export const optionalEmailSchema = z.string()
  .email('Invalid email format')
  .or(z.literal('').transform(() => undefined))
  .optional();

// Optional phone — blank, whitespace, and country-code-only values are omitted
export const optionalPhoneSchema = z.preprocess(
  (value) => normalizeOptionalPhone(value) ?? '',
  z.union([
    z.literal('').transform(() => undefined),
    phoneSchema,
  ]),
).optional();

// Teacher form schema
export const teacherFormSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchema,
  otherNames: otherNamesSchema,
  gender: genderSchema.optional(),
  dob: dobSchema,
  phoneNo: optionalPhoneSchema,
  sendInvite: z.boolean().optional(),
});

export type TeacherFormData = z.infer<typeof teacherFormSchema>;

// Student form schema (email not required)
export const studentFormSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  otherNames: otherNamesSchema,
  email: optionalEmailSchema,
  gender: genderSchema.optional(),
  dob: dobSchema,
  phoneNo: optionalPhoneSchema,
});

export type StudentFormData = z.infer<typeof studentFormSchema>;

// Guardian form schema (email or phone required; names optional)
export const guardianFormSchema = z.object({
  firstName: optionalNameSchema,
  lastName: optionalNameSchema,
  otherNames: otherNamesSchema,
  email: z.string().email('Invalid email format').or(z.literal('').transform(() => undefined)).optional(),
  gender: genderSchema.optional(),
  dob: dobSchema,
  phoneNo: optionalPhoneSchema,
}).refine(
  (data) => (data.email && data.email.length > 0) || (data.phoneNo && data.phoneNo.length > 0),
  { message: 'Email or phone number is required', path: ['email'] }
);

export type GuardianFormData = z.infer<typeof guardianFormSchema>;

// Generic user form schema (for edit modals)
export const userFormSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  otherNames: otherNamesSchema,
  email: emailSchema,
  gender: genderSchema.optional(),
  dob: dobSchema,
  phoneNo: optionalPhoneSchema,
});

export type UserFormData = z.infer<typeof userFormSchema>;

// Attached guardian schema (email or phone required; names optional)
export const attachedGuardianSchema = z.object({
  firstName: optionalNameSchema,
  lastName: optionalNameSchema,
  otherNames: otherNamesSchema,
  email: z.string().email('Invalid email format').or(z.literal('').transform(() => undefined)).optional(),
  phoneNo: optionalPhoneSchema,
  gender: genderSchema.optional(),
  dob: dobSchema,
  relationshipType: z.string().optional(),
}).refine(
  (data) => (data.email && data.email.length > 0) || (data.phoneNo && data.phoneNo.length > 0),
  { message: 'Email or phone number is required', path: ['email'] }
);

// Attached student schema (name only required)
export const attachedStudentSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  otherNames: otherNamesSchema,
  email: optionalEmailSchema,
  phoneNo: optionalPhoneSchema,
  gender: genderSchema.optional(),
  dob: dobSchema,
  relationshipType: z.string().optional(),
});

// Array-level attached user schemas
export const attachedGuardiansSchema = z.array(attachedGuardianSchema);
export const attachedStudentsSchema = z.array(attachedStudentSchema);
